import { useState, useRef, useEffect, type TextareaHTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '@/components/DocMarkdown/doc-markdown.css';

interface ExpandableTextareaProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'onChange'
> {
  value: string;
  onChange: (value: string) => void;
  /** Collapsed row count */
  collapsedRows?: number;
  /** Enable markdown preview toggle in expanded mode */
  markdownPreview?: boolean;
}

export function ExpandableTextarea({
  value,
  onChange,
  collapsedRows = 2,
  markdownPreview = false,
  className,
  ...props
}: ExpandableTextareaProps) {
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState<'edit' | 'preview'>('edit');
  const expandedRef = useRef<HTMLTextAreaElement>(null);

  // Focus the expanded textarea when it opens
  useEffect(() => {
    if (expanded && expandedRef.current && tab === 'edit') {
      const end = expandedRef.current.value.length;
      expandedRef.current.focus();
      expandedRef.current.setSelectionRange(end, end);
    }
  }, [expanded, tab]);

  return (
    <>
      {/* Collapsed inline textarea with expand button */}
      <div className="relative">
        <textarea
          {...props}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={collapsedRows}
          className={cn(
            'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none pr-9',
            className
          )}
        />
        <button
          type="button"
          title="展开编辑"
          onClick={() => setExpanded(true)}
          className="absolute bottom-1.5 right-1.5 flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 3h6v6" />
            <path d="M9 21H3v-6" />
            <path d="M21 3l-7 7" />
            <path d="M3 21l7-7" />
          </svg>
        </button>
      </div>

      {/* Expanded overlay */}
      {expanded
        ? createPortal(
            <div
              className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in-0 duration-150"
              onClick={() => setExpanded(false)}
            >
              <div
                className="relative flex max-h-[80vh] w-[min(720px,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border bg-background shadow-xl animate-in zoom-in-95 duration-150"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between gap-2 border-b px-5 py-3">
                  <div className="flex items-center gap-1">
                    {markdownPreview ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setTab('edit')}
                          className={cn(
                            'flex h-7 items-center rounded-md px-3 text-sm font-medium transition-colors',
                            tab === 'edit'
                              ? 'bg-primary/10 text-primary'
                              : 'text-muted-foreground hover:bg-muted'
                          )}
                        >
                          编辑
                        </button>
                        <button
                          type="button"
                          onClick={() => setTab('preview')}
                          className={cn(
                            'flex h-7 items-center rounded-md px-3 text-sm font-medium transition-colors',
                            tab === 'preview'
                              ? 'bg-primary/10 text-primary'
                              : 'text-muted-foreground hover:bg-muted'
                          )}
                        >
                          预览
                        </button>
                      </>
                    ) : (
                      <span className="text-sm font-semibold text-foreground">
                        {props.placeholder ?? '编辑'}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    title="关闭"
                    onClick={() => setExpanded(false)}
                    className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 6 6 18" />
                      <path d="M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Body */}
                {tab === 'edit' || !markdownPreview ? (
                  <textarea
                    ref={expandedRef}
                    {...props}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    rows={12}
                    className="w-full resize-y border-0 bg-background px-5 py-4 text-sm leading-relaxed ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                  />
                ) : (
                  <div className="overflow-y-auto px-5 py-4">
                    {value.trim() ? (
                      <div className="doc-markdown break-words">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">暂无内容</p>
                    )}
                  </div>
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
